import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import * as DealDetailsUtils from "utils/dealDetails";

import { DeviceTypes, StockStatus } from "../../config/constants";
import labels from "../../config/labels";

/**
 * @typedef {Object} StockState
 * @property {boolean} isInStock - unique id
 * @property {boolean} isBackOrder - name of color
 */

/**
 * Method to get the stock status of the primary (and secondary for double deal) device
 *
 * @returns {StockState} true if device (both) is IN_STOCK, false if device (both) is OUT_OF_STOCK
 */
export const useDeviceStockStatus = () => {
  const [isInStock, setIsInStock] = useState(false);
  const [isBackOrder, setIsBackOrder] = useState(false);

  const selectedPrimaryDeviceColor = useSelector((state) => state.dealDetails.selectedPrimaryDeviceColor);
  const selectedSecondaryDeviceColor = useSelector((state) => state.dealDetails.selectedSecondaryDeviceColor);
  const isSimOnly = useSelector((state) => !!state.dealDetails.selectedDealDetails?.sim_only_flg);

  useEffect(() => {

    const isPrimaryDeviceInStock = selectedPrimaryDeviceColor?.product?.stock_status === StockStatus.IN_STOCK
      || isSimOnly;
    const isPrimaryDeviceInBackOrder = isPrimaryDeviceInStock
      && selectedPrimaryDeviceColor?.product?.quantity <= 0

    if (!selectedSecondaryDeviceColor || Object.values(selectedSecondaryDeviceColor).length === 0) {
      setIsInStock(isPrimaryDeviceInStock);
      setIsBackOrder(isPrimaryDeviceInBackOrder);
    } else {
      const isSecondaryDeviceInStock = selectedSecondaryDeviceColor?.product?.stock_status === StockStatus.IN_STOCK;
      const isSecondaryDeviceInBackOrder = isPrimaryDeviceInStock
        && selectedPrimaryDeviceColor?.product?.quantity <= 0

      setIsInStock(isPrimaryDeviceInStock && isSecondaryDeviceInStock);
      setIsBackOrder(isSecondaryDeviceInBackOrder);
    }
  }, [selectedPrimaryDeviceColor, selectedSecondaryDeviceColor]);

  return { isInStock, isBackOrder};
}

export const useDealInCartStatus = () => {
  const [isDealInCart, setDealInCart] = useState(false);

  const cartProductItems = useSelector((state) => state.cartDetails.cartProductItems);
  const selectedDealDetails = useSelector((state) => state.dealDetails.selectedDealDetails);

  useEffect(() => {
    if (
      !Array.isArray(cartProductItems)
      || !cartProductItems.length
      || !selectedDealDetails
      || Object.values(selectedDealDetails).length === 0
    ) {
      setDealInCart(false);

      return;
    }

    const isDealInCart = cartProductItems.some((cartProduct) => (
      cartProduct?.product?.is_deal_product === 1
      && cartProduct?.product?.sku === selectedDealDetails.sku
    ));

    setDealInCart(isDealInCart);
  }, [selectedDealDetails, cartProductItems]);

  return isDealInCart;
}

/**
 * Method to get the stock status of the all inclusion devices with color options
 *
 * @returns {boolean} true if device (both) is IN_STOCK, false if device (both) is OUT_OF_STOCK
 */
export const useInclusionStockStatus = () => {
  const [isInStock, setIsInStock] = useState(false);
  const [isBackOrder, setIsBackOrder] = useState(false);

  const dealInclusionColors = useSelector((state) => state.dealDetails.dealInclusionColors);

  useEffect(() => {
    if(!dealInclusionColors || !Array.isArray(Object.values(dealInclusionColors))) {
      setIsInStock(true);
      setIsBackOrder(false);
    } else {
      const selectedInclusionColors = Object.values(dealInclusionColors).map(ic => {
        return ic.selectedColor});
      const isOutOfStock = selectedInclusionColors.some(
        ic => ic?.product?.stock_status !== StockStatus.IN_STOCK
      );
      const isInBackOrder = selectedInclusionColors.some(
        ic => ic?.product?.stock_status === StockStatus.IN_STOCK && ic?.product?.quantity <= 0
      );

      setIsInStock(!isOutOfStock);
      setIsBackOrder(isInBackOrder);
    }
  }, [dealInclusionColors]);

  return { isInStock, isBackOrder};
}

export const useCartBundleOptions = () => {
  const [bundleOptions, setBundleOptions] = useState([]);

  const dealDetails = useSelector(state => state.dealDetails?.selectedDealDetails);
  const selectedPrimaryDeviceColor = useSelector(state => state.dealDetails?.selectedPrimaryDeviceColor);
  const selectedSecondaryDeviceColor = useSelector(state => state.dealDetails?.selectedSecondaryDeviceColor);
  const dealInclusionColors = useSelector(state => state.dealDetails?.dealInclusionColors);

  useEffect(() => {
    if (!Array.isArray(dealDetails?.items)) {
      return;
    }

    const productSelections = DealDetailsUtils.createColorSelections({
      selectedPrimaryDeviceColor,
      selectedSecondaryDeviceColor,
      dealInclusionColors
    });

    const bundleOptions = [];

    dealDetails.items.forEach((bundle) => {
      const configs = DealDetailsUtils.createColorSelectionConfigs(bundle?.options, productSelections)
      const values = bundle?.options.map((option) => option.id);

      const bundleOption = {
        id: bundle.option_id,
        quantity: labels.lbl_cart_min_product_add_qty,
        value: values,
      }

      if (configs.count > 0) {
        const optionItems = configs.products.map(prodConfig => ({
          ...bundleOption,
          value: configs.count > 1 ? [prodConfig.id] : bundleOption.value,
          selected_configurable_option: prodConfig,
        }));

        bundleOptions.push(...optionItems);
      } else {
        bundleOptions.push(bundleOption);
      }
    });

    setBundleOptions(bundleOptions);
  }, [dealDetails, selectedPrimaryDeviceColor, selectedSecondaryDeviceColor, dealInclusionColors]);

  return bundleOptions;
}

export const useDealSelectionSummary = () => {
  const [dealSelectionSum, setDealSelectionSum] = useState([]);

  const isSimOnly = useSelector(state => state.dealDetails?.selectedDealDetails?.sim_only_flg);
  const primaryProduct = useSelector(state => state.dealDetails?.primaryProduct);
  const secondaryProduct = useSelector(state => state.dealDetails?.secondaryProduct);

  const selectedPrimaryDeviceColor = useSelector(state => state.dealDetails?.selectedPrimaryDeviceColor);
  const selectedSecondaryDeviceColor = useSelector(state => state.dealDetails?.selectedSecondaryDeviceColor);

  const dealInclusionColors = useSelector(state => state.dealDetails?.dealInclusionColors);

  useEffect(() => {
    if (!primaryProduct || !Object.keys(primaryProduct).length) {
      return;
    }

    const dealProducts = [];

    // Primary device
    if (selectedPrimaryDeviceColor && Object.values(selectedPrimaryDeviceColor).length) {
      dealProducts.push({
        groupid: primaryProduct.sku,
        line500CODE: selectedPrimaryDeviceColor.product?.sku,
        deviceType: DeviceTypes.PrimaryDevice,
        color: selectedPrimaryDeviceColor.value,
        colorLabel: selectedPrimaryDeviceColor.label
      });
    } else {
      dealProducts.push({
        groupid: primaryProduct.sku,
        line500CODE: primaryProduct.sku,
        deviceType: DeviceTypes.PrimaryDevice,
        color: '',
        colorLabel: ''
      });
    }

    if (isSimOnly) {
      setDealSelectionSum(dealProducts);

      return;
    }

    // Secondary Device (for double deal only)
    if (selectedSecondaryDeviceColor && Object.values(selectedSecondaryDeviceColor).length) {
      dealProducts.push({
        groupid: secondaryProduct.sku,
        line500CODE: selectedSecondaryDeviceColor.product?.sku,
        deviceType: DeviceTypes.SecondaryDevice,
        color: selectedSecondaryDeviceColor.value,
        colorLabel: selectedSecondaryDeviceColor.label
      });
    }

    // Deal Inclusions
    if (Object.values(dealInclusionColors).length > 0) {
      const inclusionColorCodes = Object.values(dealInclusionColors).map(dic => (
        {
          groupid: dic?.groupId,
          line500CODE: dic?.selectedColor?.product?.sku,
          deviceType: DeviceTypes?.Inclusion,
          color: dic?.selectedColor?.value,
          colorLabel: dic?.selectedColor?.label
        }
      ));

      dealProducts.push(...inclusionColorCodes);
    }

    setDealSelectionSum(dealProducts);
  }, [
    isSimOnly,
    primaryProduct,
    selectedPrimaryDeviceColor,
    selectedSecondaryDeviceColor,
    dealInclusionColors,
  ]);

  return dealSelectionSum;
}

export const useProductTaggingInfo = () => {
  const [productTagInfo, setProductTagInfo] = useState({});

  const isSimOnly = useSelector(state => state.dealDetails?.selectedDealDetails?.sim_only_flg);

  const primaryProduct = useSelector(state => state.dealDetails?.primaryProduct);
  const secondaryProduct = useSelector(state => state.dealDetails?.secondaryProduct);

  const selectedPrimaryDeviceColor = useSelector(state => state.dealDetails?.selectedPrimaryDeviceColor);
  const selectedSecondaryDeviceColor = useSelector(state => state.dealDetails?.selectedSecondaryDeviceColor);

  useEffect(() => {
    if (!primaryProduct || !selectedPrimaryDeviceColor) {
      return;
    }

    const newProductTagInfo = {
      product_sku: [],
      product_colour: [],
      product_capacity: [],
      product_brand: [],
    };

    newProductTagInfo.product_sku.push(primaryProduct.sku);

    if (isSimOnly) {
      setProductTagInfo(newProductTagInfo);

      return;
    }

    newProductTagInfo.product_colour.push(selectedPrimaryDeviceColor?.label);
    newProductTagInfo.product_capacity.push(primaryProduct.internal_storage_label);
    newProductTagInfo.product_brand.push(primaryProduct.manufacturer_label);

    if (secondaryProduct && !!Object.keys(secondaryProduct).length) {

      newProductTagInfo.product_sku.push(secondaryProduct.sku)
      newProductTagInfo.product_colour.push(selectedSecondaryDeviceColor?.label);
      newProductTagInfo.product_capacity.push(secondaryProduct.internal_storage_label);
      newProductTagInfo.product_brand.push(secondaryProduct.manufacturer_label);
    }

    setProductTagInfo(newProductTagInfo);
  }, [
    primaryProduct,
    secondaryProduct,
    selectedPrimaryDeviceColor,
    selectedSecondaryDeviceColor,
    isSimOnly,
  ]);

  return productTagInfo;
}
